/**
 * Brand resolver — maps organizations.product to customer-facing brand config.
 *
 * LimeCall is a *brand* on the shared LimePhone infra (same backend/DB/Twilio/
 * Vapi). Theming + copy are resolved from `product`; feature access is NOT.
 * Keep this the single source of truth for brand name / colors / support email
 * so the dashboard and (later) transactional emails stay in sync.
 */

import { brandFromHost as sharedBrandFromHost } from "@shared/brandHosts";
export type { BrandProduct } from "@shared/brandHosts";
import type { BrandProduct } from "@shared/brandHosts";

export interface BrandTheme {
  /** organizations.product value this theme is for. */
  product: BrandProduct;
  /** Display name used in the dashboard chrome + copy. */
  name: string;
  /** Short tagline for the dashboard header / empty states. */
  tagline: string;
  /** Support inbox surfaced in the UI. */
  supportEmail: string;
  /** Primary accent (hex) — mirrors the CSS var set applied via themeClass. */
  accent: string;
  /** CSS class that scopes the brand's CSS variables (see index.css). "" = default. */
  themeClass: string;
  /**
   * CSS class for the MOBILE app, which has its own dark palette rather than
   * inheriting the light one the desktop themes are built on — so it needs its
   * own class per brand, not a reuse of `themeClass`. See `.mobile-*-theme` in
   * index.css.
   */
  mobileThemeClass: string;
  /**
   * The wordmark split into its two halves, because the chrome renders the
   * first in the accent colour and the second in the foreground ("Lime" +
   * "Phone"). Stored explicitly rather than derived by splitting `name` — no
   * rule about where a brand name breaks survives contact with a third brand.
   */
  wordmark: readonly [string, string];
}

const THEMES: Record<BrandProduct, BrandTheme> = {
  limephone: {
    product: "limephone",
    name: "LimePhone",
    tagline: "Your business phone system",
    supportEmail: "support@limephone.io",
    accent: "#3B82F6",
    themeClass: "",
    mobileThemeClass: "mobile-lime-theme",
    wordmark: ["Lime", "Phone"],
  },
  limecall: {
    product: "limecall",
    name: "LimeCall",
    tagline: "Website callbacks + AI voice, on autopilot",
    supportEmail: "support@limecall.com",
    accent: "#0071E3",
    themeClass: "limecall-theme",
    mobileThemeClass: "mobile-lime-theme",
    wordmark: ["Lime", "Call"],
  },
  outbound: {
    product: "outbound",
    name: "LimePhone Outbound",
    tagline: "Outbound calling & texting at scale",
    supportEmail: "support@limephone.io",
    accent: "#3B82F6",
    themeClass: "",
    mobileThemeClass: "mobile-lime-theme",
    wordmark: ["Lime", "Phone Outbound"],
  },
  /**
   * Handset runs the MOBILE app in this repo, skinned — same pattern as
   * LimeCall: one codebase, one backend, a per-brand palette. It is the mobile
   * dialer, inbox, voicemail and billing that already work here, wearing
   * Handset's colours, rather than a second implementation of all of it.
   *
   * `themeClass` is deliberately empty: the desktop themes are built on a light
   * base Handset has no design for, and Handset is a mobile-first product. Only
   * `mobileThemeClass` is populated.
   *
   * Keep this in step with brandEmailMeta in server/lib/brandEmail.ts, which is
   * the twin of this table.
   */
  handset: {
    product: "handset",
    name: "Handset",
    tagline: "The phone line with AI built in",
    supportEmail: "support@limephone.io",
    accent: "#0E7490",
    themeClass: "",
    mobileThemeClass: "mobile-handset-theme",
    wordmark: ["Hand", "set"],
  },
};

/** Resolve a brand theme from a product string; falls back to LimePhone. */
export function brandTheme(product?: string | null): BrandTheme {
  if (product && product in THEMES) return THEMES[product as BrandProduct];
  return THEMES.limephone;
}

/** Convenience: true when the org is on the LimeCall brand. */
export function isLimecall(product?: string | null): boolean {
  return product === "limecall";
}

/**
 * Host → brand resolver. Per the white-label plan, a brand's dashboard is served
 * by the *same* backend under its own hostname (app.limecall.com), so the brand
 * is keyed off window.location.hostname. Returns null for the default
 * (LimePhone) host so callers can no-op.
 *
 * The host table itself lives in `@shared/brandHosts` and is used by the SERVER
 * too — the signup path writes `organizations.product` from the same function.
 * It used to have its own regex here and its own one-liner there, which is how
 * the two came to disagree about which hosts exist.
 */
export function brandFromHost(hostname?: string): BrandProduct | null {
  return sharedBrandFromHost(
    hostname ?? (typeof window !== "undefined" ? window.location.hostname : ""),
  );
}

/** True when the app is being served under a LimeCall-branded hostname. */
export function isLimecallHost(hostname?: string): boolean {
  return brandFromHost(hostname) === "limecall";
}

/**
 * The brand to render as: HOST first, the org's `product` second.
 *
 * That order matters. The hostname is known before any network call, so a
 * branded host paints correct colours on the first frame; resolving from the
 * organisation alone would flash LimePhone blue on every cold load while the
 * query is in flight.
 *
 * The product is still consulted because Handset has no branded hostname yet —
 * without that fallback its skin would be unreachable until DNS exists.
 *
 * Falls back to LimePhone, which is what the chrome hardcoded before this
 * existed, so an unrecognised brand renders exactly as it always did.
 *
 * This is the ONLY brand resolver the UI should call. Reading `product` alone
 * gets the host case wrong and reading the host alone gets Handset wrong.
 */
export function resolveBrand(product?: string | null, hostname?: string): BrandTheme {
  return brandTheme(brandFromHost(hostname) ?? product);
}
